import { useCallback, useMemo } from 'react';

import {
  IntegrationDomain,
  IntegrationVersion
} from 'api/usePublisherIntegrationTest';
import { EEventsType } from 'constants/enums';
import { useDashboardAnalyticsTracking } from 'hooks/useDashboardAnalyticsTracking';

import { ESaveResultStatus, ISaveResult } from './useCallbackForms';

interface IWithUrl {
  url: string;
}

interface ITrackTestExecutedParams extends IWithUrl {
  statusCode: number;
}

interface ITrackIntegrationSavedParams extends IWithUrl {
  status: ESaveResultStatus;
  api: string;
  version: IntegrationVersion;
}

interface IUseApiTestingAnalyticsParams {
  domain: IntegrationDomain;
  version: IntegrationVersion;
}

const ANALYTICS_TAB = 'settings';
const ANALYTICS_PAGE = 'developer settings';

export const useApiTestingAnalytics = ({
  domain,
  version
}: IUseApiTestingAnalyticsParams) => {
  const bigQueryDefaults = useMemo(
    () => ({ api: domain, version }),
    [domain, version]
  );
  const mixpanelDefaults = useMemo(
    () => ({ api: domain, version }),
    [domain, version]
  );

  const { trackEvent } = useDashboardAnalyticsTracking({
    tab: ANALYTICS_TAB,
    page: ANALYTICS_PAGE,
    bigQueryDefaults,
    mixpanelDefaults
  });

  const trackTestModalOpened = useCallback(
    ({ url }: IWithUrl) => {
      trackEvent(EEventsType.DASHBOARD_API_TEST_OPEN_MODAL, {
        bigQueryProperties: { url },
        mixpanelProperties: { url }
      });
    },
    [trackEvent]
  );

  const trackTestExecuted = useCallback(
    ({ statusCode, url }: ITrackTestExecutedParams) => {
      trackEvent(EEventsType.DASHBOARD_API_TEST_CLICKED, {
        bigQueryProperties: { responseCode: statusCode, url },
        mixpanelProperties: { response_code: statusCode, url }
      });
    },
    [trackEvent]
  );

  const trackIntegrationSaved = useCallback(
    ({ url, status, api, version: savedVersion }: ITrackIntegrationSavedParams) => {
      trackEvent(EEventsType.DASHBOARD_API_INTEGRATION_SAVED, {
        bigQueryProperties: { api, version: savedVersion, url, status },
        mixpanelProperties: { api, version: savedVersion, url, status }
      });
    },
    [trackEvent]
  );

  const trackDocsClicked = useCallback(
    (docsUrl: string) => {
      trackEvent(EEventsType.DASHBOARD_API_DOCS_CLICKED, {
        bigQueryProperties: { url: docsUrl },
        mixpanelProperties: { url: docsUrl }
      });
    },
    [trackEvent]
  );

  const handleSaveResult = useCallback(
    ({ status, url }: ISaveResult) => {
      trackIntegrationSaved({ url, status, api: domain, version });
    },
    [trackIntegrationSaved, domain, version]
  );

  return {
    trackIntegrationSaved,
    trackTestModalOpened,
    trackTestExecuted,
    trackDocsClicked,
    handleSaveResult
  };
};
